import React from 'react';

/**
 * Split form of Transcript's `headerExtras` prop (PLAT-11039): `filters`
 * renders inside the header's content-filter group (after Quotes / Clips /
 * Highlights — how the T&S tab keeps Story Topics beside the other filters),
 * `trailing` in the right-pinned tail. A plain ReactNode is treated as
 * `trailing`, so hosts that only pass translation UI need no change.
 */
/**
 * Transcript's filter state, for a host whose own filter has to react to it.
 * Read during render — never pushed up through an `on*Change` prop, which is
 * how the highlight-ranges signal turned into a render loop (PLAT-10949) and
 * later into an unreliable Show Only guard (PLAT-11182).
 */
export type FilterSlotState = {
  /** The Highlights dropdown's mode toggle reads "Show Only". */
  highlightsShowOnlyActive: boolean;
};

/**
 * The filters slot takes a function when the host's filter needs Transcript's
 * filter state (the T&S tab's Story Topics dropdown, which goes inert while
 * Highlights owns Show Only); a plain node otherwise.
 */
export type FiltersSlot =
  | React.ReactNode
  | ((state: FilterSlotState) => React.ReactNode);

export type HeaderExtrasSlots = {
  filters?: FiltersSlot;
  /**
   * The host's filter in the slot owns Show Only right now (PLAT-11182). The
   * mirror of `FilterSlotState`: it keys the Highlights trigger's inert state
   * on the mode itself, so the block holds from the moment the toggle flips —
   * before a topic is picked, when the visibility set is still undefined.
   */
  filtersOwnShowOnly?: boolean;
  trailing?: React.ReactNode;
};

/** A function is never a valid ReactNode, so it discriminates the two forms. */
export const resolveFilters = (
  slot: FiltersSlot,
  state: FilterSlotState,
): React.ReactNode => (typeof slot === 'function' ? slot(state) : slot);

/** Distinguishes the slots object from renderable nodes (elements, arrays). */
export const isHeaderExtrasSlots = (
  value: React.ReactNode | HeaderExtrasSlots,
): value is HeaderExtrasSlots =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  !React.isValidElement(value) &&
  ('filters' in value || 'trailing' in value);
